import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  BillingRate, ScopeType, BillingStrategy, DEFAULT_RATES, SERVICE_LABELS, STRATEGY_LABELS,
  getRates, setRate, deleteRate, resolveAllRates, getFlatRate, setFlatRate, invalidateRatesCache,
} from '../../api/billingRates';

interface RatesTableProps {
  scopeType: ScopeType;
  scopeId?: number | string;
  propertyId?: number | string;
  customerId?: number | string;
  dealerId?: number | string;
  readOnly?: boolean;
  showStrategy?: boolean; // show billing strategy selector
}

const fmtDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export const RatesTable: React.FC<RatesTableProps> = ({
  scopeType, scopeId, propertyId, customerId, dealerId, readOnly, showStrategy
}) => {
  const [rates, setRatesState] = useState<BillingRate[]>([]);
  const [resolved, setResolved] = useState<Record<string, { cents: number; source: ScopeType; sourceId?: number | string; strategy: BillingStrategy }>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editStrategy, setEditStrategy] = useState<BillingStrategy>('counter');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [flatRate, setFlatRateState] = useState<number | null>(null);
  const [editingFlatRate, setEditingFlatRate] = useState(false);
  const [flatRateValue, setFlatRateValue] = useState('');

  const fetchRates = async () => {
    setLoading(true);
    try {
      const [localRates, resolvedRates] = await Promise.all([
        getRates(scopeType, scopeId),
        resolveAllRates(propertyId, customerId, dealerId),
      ]);
      setRatesState(localRates);
      setResolved(resolvedRates);

      // Check flat rate
      if (scopeId && (scopeType === 'customer' || scopeType === 'property')) {
        const fr = await getFlatRate(scopeType, scopeId);
        setFlatRateState(fr);
      }
    } catch (e) {
      console.error('Failed to load rates:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, [scopeType, scopeId]);

  const handleSave = async (serviceKey: string) => {
    const cents = Math.round(parseFloat(editValue) * 100);
    if (isNaN(cents) || cents < 0) return;
    setSaving(true);
    try {
      await setRate(scopeType, scopeId || null, serviceKey, cents, {
        billing_strategy: editStrategy,
      });
      await fetchRates();
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (rateId: number) => {
    if (!window.confirm('Reset this rate to inherited value?')) return;
    setSaving(true);
    try {
      await deleteRate(rateId);
      await fetchRates();
    } finally {
      setSaving(false);
    }
  };

  const handleFlatRateSave = async () => {
    const cents = Math.round(parseFloat(flatRateValue) * 100);
    if (isNaN(cents) || cents < 0 || !scopeId) return;
    setSaving(true);
    try {
      await setFlatRate(scopeType, scopeId, cents);
      setFlatRateState(cents);
      setEditingFlatRate(false);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFlatRate = async () => {
    if (!scopeId) return;
    const existing = rates.find(r => r.service_key === 'flat_rate_total');
    if (existing) {
      await deleteRate(existing.id);
      setFlatRateState(null);
      await fetchRates();
    }
  };

  const sourceLabel = (source: ScopeType) => {
    const labels: Record<ScopeType, string> = { standard: 'Standard', online: 'Online', dealer: 'Dealer', customer: 'Customer', property: 'Property' };
    return labels[source];
  };

  if (loading) return <div className="p-4 text-gray-400 text-sm">Loading rates...</div>;

  return (
    <div>
      {/* Flat Rate Option */}
      {scopeId && (scopeType === 'customer' || scopeType === 'property') && !readOnly && (
        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-emerald-900">Flat Rate Pricing</span>
              <p className="text-xs text-emerald-600">Charge a single monthly price for all services</p>
            </div>
            {flatRate != null ? (
              <div className="flex items-center gap-2">
                <Badge color="blue">Active: {fmtDollars(flatRate)}/mo</Badge>
                <button className="text-xs text-red-600 hover:underline" onClick={handleRemoveFlatRate}>Remove</button>
              </div>
            ) : editingFlatRate ? (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <input type="number" step="0.01" value={flatRateValue} onChange={e => setFlatRateValue(e.target.value)} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" autoFocus />
                <span className="text-xs text-gray-500">/mo</span>
                <Button size="sm" onClick={handleFlatRateSave} disabled={saving}>Set</Button>
                <Button size="sm" variant="secondary" onClick={() => setEditingFlatRate(false)}>Cancel</Button>
              </div>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => { setEditingFlatRate(true); setFlatRateValue(''); }}>Enable Flat Rate</Button>
            )}
          </div>
        </div>
      )}

      {/* Per-Service Rates */}
      <div className="divide-y divide-gray-100">
        <div className={`grid ${showStrategy ? 'grid-cols-7' : 'grid-cols-5'} gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50`}>
          <span>Service</span>
          <span>Description</span>
          <span>Rate</span>
          {showStrategy && <span>Strategy</span>}
          <span>Source</span>
          {showStrategy && <span>Active</span>}
          <span>Actions</span>
        </div>
        {Object.keys(DEFAULT_RATES).map(key => {
          const localRate = rates.find(r => r.service_key === key);
          const resolvedRate = resolved[key];
          const isOverridden = !!localRate;
          const displayCents = localRate?.unit_price_cents ?? resolvedRate?.cents ?? DEFAULT_RATES[key].cents;
          const source = localRate ? scopeType : resolvedRate?.source || 'standard';
          const strategy = localRate?.billing_strategy || resolvedRate?.strategy || 'counter';

          return (
            <div key={key} className={`grid ${showStrategy ? 'grid-cols-7' : 'grid-cols-5'} gap-2 px-4 py-3 text-sm items-center`}>
              <span className="font-medium text-gray-900">{SERVICE_LABELS[key] || key}</span>
              <span className="text-gray-500 text-xs">{DEFAULT_RATES[key].description}</span>
              <span>
                {editing === key ? (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <input type="number" step="0.01" min="0" value={editValue} onChange={e => setEditValue(e.target.value)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm" autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleSave(key); if (e.key === 'Escape') setEditing(null); }}
                    />
                  </div>
                ) : (
                  <span className={`font-medium ${isOverridden ? 'text-brand-600' : 'text-gray-900'}`}>
                    {fmtDollars(displayCents)}
                  </span>
                )}
              </span>
              {showStrategy && (
                <span>
                  {editing === key ? (
                    <select value={editStrategy} onChange={e => setEditStrategy(e.target.value as BillingStrategy)} className="text-xs border border-gray-300 rounded px-1 py-1">
                      {Object.entries(STRATEGY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  ) : (
                    <span className="text-xs text-gray-500">{STRATEGY_LABELS[strategy]}</span>
                  )}
                </span>
              )}
              <span>
                <Badge color={isOverridden ? 'blue' : 'gray'}>{isOverridden ? 'Override' : sourceLabel(source)}</Badge>
              </span>
              {showStrategy && <span className="text-gray-500">—</span>}
              <span>
                {readOnly ? null : editing === key ? (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => handleSave(key)} disabled={saving}>Save</Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <button className="text-xs text-brand-600 hover:underline"
                      onClick={() => { setEditing(key); setEditValue((displayCents / 100).toFixed(2)); setEditStrategy(strategy); }}>
                      Edit
                    </button>
                    {isOverridden && (
                      <button className="text-xs text-red-600 hover:underline ml-2" onClick={() => handleReset(localRate!.id)}>Reset</button>
                    )}
                  </div>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
